import { Field } from '@atlaskit/form';
import ExpandHorizontalIcon from '@atlaskit/icon/core/expand-horizontal';
import ImageFullscreenIcon from '@atlaskit/icon/core/image-fullscreen';
import { Text } from '@atlaskit/primitives';
import { messages as intlMessages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import {
  type DatabaseField,
  type DatabaseFieldType,
  MediaFormat
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import React, { type FunctionComponent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from './messages.ts';
import CompactRadioPicker, {
  type CompactRadioPickerOption
} from './shared/CompactRadioPicker.tsx';

type AttachmentFieldSettingsProps = {
  contentId: string;
  field: DatabaseField<DatabaseFieldType.ATTACHMENT>;
};

const AttachmentFieldSettings: FunctionComponent<AttachmentFieldSettingsProps> = ({
  contentId,
  field
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();

  const displayFormatLabel = formatMessage(messages.attachmentDisplaySizeLabel);
  const options: CompactRadioPickerOption<MediaFormat>[] = useMemo(
    () => [
      {
        icon: <ExpandHorizontalIcon label="" />,
        label: formatMessage(
          intlMessages['field-settings.type-attachment-size-field-autofill']
        ),
        value: MediaFormat.AUTOFILL
      },
      {
        icon: <ImageFullscreenIcon label="" />,
        label: formatMessage(
          intlMessages['field-settings.type-attachment-size-field-thumbnail']
        ),
        value: MediaFormat.THUMBNAIL
      }
    ],
    [formatMessage]
  );

  const handleMediaFormatChange = useCallback(
    (mediaFormat: MediaFormat) => {
      dispatch(
        addChangeToDraft(
          contentId,
          {
            type: DatabaseChangeType.SET_FIELD_SETTINGS_MEDIA_FORMAT,
            fieldId: field.id,
            mediaFormat
          },
          {
            fieldSettingsAnalytics: {
              source: 'fieldContextualMenu',
              fieldType: field.type
            }
          }
        )
      );
    },
    [contentId, dispatch, field.id, field.type]
  );

  return (
    <Field
      label={
        <Text size="small" weight="bold" color="color.text.subtle">
          {displayFormatLabel}
        </Text>
      }
      name="attachment-display-format-field"
    >
      {() => (
        <CompactRadioPicker
          ariaLabel={displayFormatLabel}
          onChange={handleMediaFormatChange}
          options={options}
          value={field.settings.mediaFormat ?? MediaFormat.AUTOFILL}
        />
      )}
    </Field>
  );
};

export default AttachmentFieldSettings;
